import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { MapPin, CalendarPlus, Calendar, Check, Repeat } from "lucide-react";
import WithDividers from "components/helpers/WithDividers";
import {
  CalendarEventGroup,
  CalendarEvent,
  CalendarEventRecurrence,
  AddedEventInfo,
} from "components/hooks/useTaskAgentState";
import { RecurringUnit } from "protogen/calendar_pb";
import { pluralize } from "common/utils";
import { useCreateMultipleCalendarEvents } from "services/calendar";
import Checkbox from "components/common/Checkbox";
import FlexBox from "components/common/FlexBox";
import LinkRouter from "components/navigation/LinkRouter";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// These strings come straight from the agent's tool call, so they may be date-only. JS parses a
// date-only string as UTC midnight, which renders as the previous day for any viewer west of UTC.
const parseEventDate = (isoString: string) => {
  if (DATE_ONLY_PATTERN.test(isoString)) {
    const [year, month, day] = isoString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(isoString);
};

// The server stores inclusive end dates and treats a midnight end as an exclusive boundary
// (calendar_utils.create_abridged_event). Mirror that, or the card claims a day more than it saves.
const lastAllDayDate = (startString: string, endString: string) => {
  const start = parseEventDate(startString);
  const end = parseEventDate(endString);
  const endsOnExclusiveBoundary =
    end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0;
  if (endsOnExclusiveBoundary) {
    end.setDate(end.getDate() - 1);
  }
  return end < start ? start : end;
};

const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const FREQUENCY_TO_UNIT: Record<
  CalendarEventRecurrence["frequency"],
  RecurringUnit
> = {
  daily: RecurringUnit.RecurringUnit_DAY,
  weekly: RecurringUnit.RecurringUnit_WEEK,
  monthly: RecurringUnit.RecurringUnit_MONTH,
  yearly: RecurringUnit.RecurringUnit_YEAR,
};

const FREQUENCY_TO_NOUN: Record<CalendarEventRecurrence["frequency"], string> =
  { daily: "day", weekly: "week", monthly: "month", yearly: "year" };

const recurrenceRequestFields = (recurrence?: CalendarEventRecurrence) => {
  if (!recurrence) {
    return {
      recurringUnit: RecurringUnit.RecurringUnit_UNSPECIFIED,
      recurringInterval: 0,
      recurringByDay: [],
      recurringUntilDate: "",
      recurringCount: 0,
    };
  }
  return {
    recurringUnit: FREQUENCY_TO_UNIT[recurrence.frequency],
    recurringInterval: recurrence.interval || 1,
    recurringByDay: (recurrence.days_of_week || []).map((day) =>
      WEEKDAYS.indexOf(day),
    ),
    recurringUntilDate: recurrence.until_date || "",
    recurringCount: recurrence.count || 0,
  };
};

const describeRecurrence = (recurrence: CalendarEventRecurrence) => {
  const interval = recurrence.interval || 1;
  const noun = FREQUENCY_TO_NOUN[recurrence.frequency];
  let description =
    interval === 1
      ? `Repeats ${recurrence.frequency}`
      : `Repeats every ${interval} ${pluralize(noun)}`;
  if (recurrence.days_of_week?.length) {
    const days = recurrence.days_of_week.map(
      (day) => day.charAt(0).toUpperCase() + day.slice(1),
    );
    description += ` on ${days.join(", ")}`;
  }
  if (recurrence.count) {
    description += `, ${recurrence.count} times`;
  } else if (recurrence.until_date) {
    description += ` until ${parseEventDate(
      recurrence.until_date,
    ).toLocaleDateString()}`;
  }
  return description;
};

const CalendarEventEntry = ({
  event,
  checked,
  onToggle,
  isAdded = false,
  addedEventInfo,
}: {
  event: CalendarEvent;
  checked: boolean;
  onToggle?: () => void;
  isAdded?: boolean;
  addedEventInfo?: AddedEventInfo;
}) => {
  const formatTime = (isoString: string) => {
    try {
      const date = parseEventDate(isoString);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch {
      return isoString;
    }
  };

  const formatDate = (isoString: string) => {
    const date = parseEventDate(isoString);
    return date.toLocaleDateString();
  };

  const allDayStart = formatDate(event.start_time);
  const allDayEnd = event.end_time
    ? lastAllDayDate(event.start_time, event.end_time).toLocaleDateString()
    : allDayStart;

  return (
    <FlexBox
      variant="contained"
      gap="16px"
      sx={{ backgroundColor: "#FAF9FA", borderColor: "#F2F2F2" }}
    >
      <FlexBox
        direction="column"
        gap="4px"
        sx={{ flex: 1, overflow: "hidden" }}
      >
        {addedEventInfo?.eventRef ? (
          <Box display="flex" alignItems="center" flexDirection="row" gap="4px">
            <Typography variant="bodyHeavy">
              <LinkRouter
                to={`/calendar/${encodeURIComponent(addedEventInfo.eventRef)}`}
                useColorScheme
              >
                {event.title}
              </LinkRouter>
            </Typography>
            {addedEventInfo.eventModified && (
              <Typography variant="bodySmall" color="text.secondary">
                (Modified)
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="bodyHeavy">{event.title}</Typography>
        )}

        <Box display="flex" alignItems="center" gap="4px">
          <Calendar size={16} color="#8E9598" />
          {event.all_day ? (
            <Typography variant="body">
              {allDayStart === allDayEnd ? (
                allDayStart
              ) : (
                <>
                  {allDayStart} - {allDayEnd}
                </>
              )}{" "}
              (All day)
            </Typography>
          ) : (
            <Typography variant="bodySmall">
              {formatTime(event.start_time)} - {formatTime(event.end_time)}
            </Typography>
          )}
          <Typography variant="bodySmall" color="text.secondary">
            ({event.timezone})
          </Typography>
        </Box>

        {event.recurrence && (
          <Box display="flex" alignItems="center" gap="4px">
            <Repeat size={16} color="#8E9598" />
            <Typography variant="bodySmall">
              {describeRecurrence(event.recurrence)}
            </Typography>
          </Box>
        )}

        {event.location && (
          <Box display="flex" alignItems="center" gap="4px">
            <MapPin size={16} color="#8E9598" />
            <Typography variant="bodySmall">{event.location}</Typography>
          </Box>
        )}

        {event.description && (
          <Typography variant="bodySmall" color="text.secondary">
            {event.description}
          </Typography>
        )}
      </FlexBox>
      {isAdded ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {addedEventInfo?.eventStatus === "deleted" ? (
            <Typography variant="bodySmallHeavy" color="error.main">
              Removed
            </Typography>
          ) : (
            <>
              <Check size={20} color="#468E3E" />
              <Typography variant="bodySmallHeavy" color="success.main">
                Added
              </Typography>
            </>
          )}
        </Box>
      ) : (
        onToggle && (
          <Checkbox checked={checked} onChange={onToggle} sx={{ padding: 0 }} />
        )
      )}
    </FlexBox>
  );
};

export const CalendarEventsSection = ({
  calendarEventGroups,
  familyRef,
  onAddEvents,
  agentThreadRef,
}: {
  calendarEventGroups: CalendarEventGroup[];
  familyRef?: string;
  onAddEvents?: (events: CalendarEvent[], calendarName: string) => void;
  agentThreadRef?: string;
}) => {
  const [addedEvents, setAddedEvents] = useState<Record<string, Set<number>>>(
    {},
  );
  const [selectedEvents, setSelectedEvents] = useState<
    Record<string, Set<number>>
  >({});
  const { request, loading, error } = useCreateMultipleCalendarEvents();

  useEffect(() => {
    const newAddedEvents: Record<string, Set<number>> = {};
    const newSelectedEvents: Record<string, Set<number>> = {};

    calendarEventGroups.forEach((group) => {
      const alreadyAdded = group.addedEventIndices || new Set();

      if (alreadyAdded.size > 0) {
        newAddedEvents[group.id] = new Set(alreadyAdded);
      }

      if (alreadyAdded.size > 0) {
        newSelectedEvents[group.id] = new Set(
          group.events
            .map((_, index) => index)
            .filter((index) => !alreadyAdded.has(index)),
        );
      } else {
        newSelectedEvents[group.id] = new Set(
          group.events.map((_, index) => index),
        );
      }
    });

    const areSetsEqual = (
      a: Record<string, Set<number>>,
      b: Record<string, Set<number>>,
    ) => {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => {
        const setA = a[key];
        const setB = b[key];
        if (!setA || !setB) return setA === setB;
        if (setA.size !== setB.size) return false;
        return Array.from(setA).every((val) => setB.has(val));
      });
    };

    setAddedEvents((prev) => {
      return areSetsEqual(prev, newAddedEvents) ? prev : newAddedEvents;
    });
    setSelectedEvents((prev) => {
      return areSetsEqual(prev, newSelectedEvents) ? prev : newSelectedEvents;
    });
  }, [calendarEventGroups]);

  const toggleEventSelection = (groupId: string, eventIndex: number) => {
    setSelectedEvents((prev) => {
      const groupSelections = new Set(prev[groupId] || []);
      if (groupSelections.has(eventIndex)) {
        groupSelections.delete(eventIndex);
      } else {
        groupSelections.add(eventIndex);
      }
      return { ...prev, [groupId]: groupSelections };
    });
  };

  const handleAddAllEvents = async (group: CalendarEventGroup) => {
    if (loading) return;
    const selectedIndices = selectedEvents[group.id] || new Set();
    const eventsToAdd: Array<{ event: CalendarEvent; originalIndex: number }> =
      [];

    group.events.forEach((event, index) => {
      if (selectedIndices.has(index)) {
        eventsToAdd.push({ event, originalIndex: index });
      }
    });

    if (eventsToAdd.length === 0) return;
    if (!familyRef) return;

    await request({
      familyRef,
      calendarName: group.calendar_name,
      events: eventsToAdd.map(({ event, originalIndex }) => ({
        title: event.title,
        description: event.description || "",
        start: event.start_time,
        end: event.end_time,
        allDay: event.all_day,
        invitees: [], // Could be extended later
        displayTimezone: event.timezone,
        location: event.location || "",
        sourceIndex: originalIndex, // Preserve the original index
        ...recurrenceRequestFields(event.recurrence),
      })),
      sourceEntityType: agentThreadRef ? "agent_thread" : "",
      sourceEntityRef: agentThreadRef || "",
    });
    // Mark the specific events as added (using original indices)
    const addedIndices = eventsToAdd.map(({ originalIndex }) => originalIndex);
    setAddedEvents((prev) => ({
      ...prev,
      [group.id]: new Set([
        ...Array.from(prev[group.id] || []),
        ...addedIndices,
      ]),
    }));
    onAddEvents?.(
      eventsToAdd.map(({ event }) => event),
      group.calendar_name,
    );
  };
  if (calendarEventGroups.length === 0) return null;

  return (
    <Box>
      <WithDividers>
        {calendarEventGroups.map((group) => {
          const addedEventIndices = addedEvents[group.id] || new Set();
          const isLoading = loading;
          const selectedIndices = selectedEvents[group.id] || new Set();
          const selectedCount = Array.from(selectedIndices).filter(
            (index) => !addedEventIndices.has(index),
          ).length;
          const noneSelected = selectedCount === 0;

          const selectAll = () => {
            setSelectedEvents((prev) => ({
              ...prev,
              [group.id]: new Set(
                group.events
                  .map((_, index) => index)
                  .filter((index) => !addedEventIndices.has(index)),
              ),
            }));
          };

          const selectNone = () => {
            setSelectedEvents((prev) => ({
              ...prev,
              [group.id]: new Set(),
            }));
          };

          const allowEventUpdates =
            onAddEvents !== undefined &&
            addedEventIndices.size !== group.events.length;

          return (
            <FlexBox
              direction="column"
              key={group.id}
              sx={{ padding: "16px 0" }}
            >
              {/* Header, select all/none */}
              <FlexBox alignItems="center" justifyContent="space-between">
                <Typography variant="h4">{group.calendar_name}</Typography>
                {allowEventUpdates && (
                  <Box display="flex" alignItems="center" gap="8px">
                    <Button
                      variant="text"
                      size="small"
                      onClick={selectAll}
                      sx={{ minWidth: "auto", height: "fit-content" }}
                    >
                      All
                    </Button>
                    <Typography variant="body" color="gray.5">
                      |
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={selectNone}
                      sx={{ minWidth: "auto", height: "fit-content" }}
                    >
                      None
                    </Button>
                  </Box>
                )}
              </FlexBox>

              {/* Event list  */}
              <FlexBox direction="column">
                {group.events.map((event, index) => (
                  <CalendarEventEntry
                    key={index}
                    event={event}
                    checked={(selectedEvents[group.id] || new Set()).has(index)}
                    onToggle={
                      allowEventUpdates
                        ? () => toggleEventSelection(group.id, index)
                        : undefined
                    }
                    isAdded={addedEventIndices.has(index)}
                    addedEventInfo={group.addedEvents?.get(index)}
                  />
                ))}
              </FlexBox>

              {error && (
                <Alert severity="error">
                  Failed to add calendar events: {error}
                </Alert>
              )}

              {/* CTA button */}
              {allowEventUpdates && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleAddAllEvents(group)}
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CalendarPlus size={16} />
                    )
                  }
                  disabled={isLoading || noneSelected}
                >
                  {selectedCount === 0
                    ? "No events selected"
                    : `Add ${selectedCount} ${pluralize(
                        "event",
                        selectedCount,
                      )}`}
                </Button>
              )}
            </FlexBox>
          );
        })}
      </WithDividers>
    </Box>
  );
};
